import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  ProcedureParameterConfirmationMethod,
  ProcedureParameterUsageValueType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  ActionFunctions,
  SchemaAndColumnGroup,
  StructuredTableAdapterOptions,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTable,
  StructuredTableRow,
  StructuredTableKind,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCellError} from 'notebook/tables/excess/Evaluator';
import {Cell} from 'notebook/tables/TableConstants';
import {Table, TableColumnType, TableReorderLocks} from 'notebook/tables/TableConstants';
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  DatetimeSchemaFieldTypedValue,
  FloatSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {PARAMETER_TABLES_COLUMN_WIDTHS} from 'procedures/design/ProcedureDesignConstants';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {filterProcedureValidationErrors} from 'procedures/design/validation/procedureValidationUtils';
import {ParameterUsage} from 'procedures/ProcedureDomainTypes';
import {DataParameterTypedValue, ProcedureDataParameterSetting} from 'procedures/ProcedureDomainTypes';
import {
  DataParameterType,
  DEFAULT_DATA_PARAMETER_TYPE,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {
  convertDataParameterTypeToTypedValue,
  convertTypedValueToDataParameterType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldTypeConversionUtils';
import {makeParameterUsageCell} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import {
  BaseParameterConfigTableMetadata,
  getParameterUsageColumn,
} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import {
  makeMinMaxCell,
  makeSetpointCell,
  makeStringCell,
  makeUnitReferenceCell,
  validationErrorsToEvaluatedCellError,
} from 'procedures/tables/procedureTableAdapterUtils';
import {
  cellMinMaxTypedValue,
  cellText,
  getConfirmationMethodStructuredTableColumn,
  getConfirmationMethodValueFromCell,
  makeConfirmationMethodCell,
  makeDataParameterTypedValueFromText,
} from 'procedures/tables/procedureTableAdapterUtils';
import {ProcedureSchemaFieldTypeContext} from 'procedures/tables/ProcedureTableConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {Optional} from 'util/Types';

export interface ProcedureDataParametersConfigTableMetadata extends BaseParameterConfigTableMetadata {}

export interface ProcedureDataParametersConfigTableRowMetadata {
  apiId: string | null;
  procedureDataParameterId: string | null;
  plannedValueId: string | null;
}

export enum DataParameterConfigColumn {
  NAME = 'NAME',
  TYPE = 'TYPE',
  SETPOINT = 'SETPOINT',
  UNITS = 'UNITS',
  MINIMUM = 'MINIMUM',
  MAXIMUM = 'MAXIMUM',
  DESCRIPTION = 'DESCRIPTION',
  CONFIRMATION_METHOD = 'CONFIRMATION_METHOD',
  CONFIRMED_IN = 'CONFIRMED_IN',
  MEASURED_IN = 'MEASURED_IN',
}

/**
 * Extends {@link DataParameterType} with the library definition's unit type ID so the row
 * delegate controller creates distinct delegate column sets per unit type.
 */
export type DataParameterDelegateType = DataParameterType & {
  unitTypeId?: string;
};

export const ALL_POSSIBLE_COLUMNS_ORDERED = [
  DataParameterConfigColumn.NAME,
  DataParameterConfigColumn.TYPE,
  DataParameterConfigColumn.SETPOINT,
  DataParameterConfigColumn.UNITS,
  DataParameterConfigColumn.MINIMUM,
  DataParameterConfigColumn.MAXIMUM,
  DataParameterConfigColumn.DESCRIPTION,
  DataParameterConfigColumn.CONFIRMATION_METHOD,
  DataParameterConfigColumn.CONFIRMED_IN,
  DataParameterConfigColumn.MEASURED_IN,
];

export const GET_IS_ENABLED_BY_COLUMN: {[c in DataParameterConfigColumn]?: () => boolean} = {
  [DataParameterConfigColumn.CONFIRMATION_METHOD]: () =>
    BenchlingConfig.PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES,
};

export function columnProvenanceId(column: DataParameterConfigColumn): string {
  return `dp_${column}`;
}

export const GET_STC_BY_COLUMN: Record<
  DataParameterConfigColumn,
  (metadata: ProcedureDataParametersConfigTableMetadata) => StructuredTableColumn
> = {
  [DataParameterConfigColumn.NAME]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Name',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.NAME),
  }),
  [DataParameterConfigColumn.TYPE]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      name: 'Data type',
      isRequired: true,
      defaultValue: {
        text: '',
        typedValue: convertDataParameterTypeToTypedValue(DEFAULT_DATA_PARAMETER_TYPE),
      },
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE, // for TS
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.TYPE),
    context: ProcedureSchemaFieldTypeContext.DATA_PARAMETER,
  }),
  [DataParameterConfigColumn.SETPOINT]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: 'Setpoint',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NORMAL,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.SETPOINT),
  }),
  [DataParameterConfigColumn.UNITS]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.UNIT_REFERENCE,
      name: 'Units',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NORMAL,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.UNITS),
  }),
  [DataParameterConfigColumn.MINIMUM]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: 'Min',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NARROW,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.MINIMUM),
  }),
  [DataParameterConfigColumn.MAXIMUM]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: 'Max',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NARROW,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.MAXIMUM),
  }),
  [DataParameterConfigColumn.DESCRIPTION]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Description',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.DESCRIPTION),
  }),
  [DataParameterConfigColumn.CONFIRMED_IN]: (metadata) =>
    getParameterUsageColumn({
      metadata,
      provenanceUuid: columnProvenanceId(DataParameterConfigColumn.CONFIRMED_IN),
      columnGroup: StructuredTableColumnGroups.REFERENCE,
    }),
  [DataParameterConfigColumn.MEASURED_IN]: (metadata) =>
    getParameterUsageColumn({
      metadata,
      provenanceUuid: columnProvenanceId(DataParameterConfigColumn.MEASURED_IN),
      columnName: 'Measured in',
    }),
  [DataParameterConfigColumn.CONFIRMATION_METHOD]: () => ({
    ...getConfirmationMethodStructuredTableColumn(),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.CONFIRMATION_METHOD),
  }),
};

export function getColumnIndex(
  targetColumn: DataParameterConfigColumn,
  stColumns: Array<StructuredTableColumn>
): number {
  return stColumns.findIndex(({provenanceUuid}) => provenanceUuid === columnProvenanceId(targetColumn));
}

function isMinMaxTypedValueEmpty(
  typedValue: FloatSchemaFieldTypedValue | DatetimeSchemaFieldTypedValue | null
): boolean {
  return typedValue == null || isTypedValueEmpty(typedValue);
}

/**
 * Builds the core ProcedureDataParameterSetting from a structured table row.
 * Both the standard and library adapters share this logic; the library adapter
 * extends the result with libraryParameterDefinition (id + name).
 */
export function buildCoreDataParameterSetting(
  columns: Array<StructuredTableColumn>,
  row: StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata>
): ProcedureDataParameterSetting {
  assert(
    columns.length === row.cells.length,
    'Expected row to have same number of cells as number of columns'
  );

  const setpointCell = row.cells[getColumnIndex(DataParameterConfigColumn.SETPOINT, columns)];

  const getDataParameterTypedValue = (dataType: DataParameterType): DataParameterTypedValue => {
    const setpointCellType = setpointCell.typedValue?.type;
    const selectedType = dataType.type;
    if (setpointCellType === selectedType) {
      // User has not changed type selector, so use the setpoint typed value
      return setpointCell.typedValue!;
    }
    // If the setpoint typed value has not been set, infer the typed value from the selected type
    // If the user updated the type selector, convert the setpoint typed value
    return makeDataParameterTypedValueFromText(selectedType, setpointCell.text);
  };

  const paramTypeCell = row.cells[getColumnIndex(DataParameterConfigColumn.TYPE, columns)];
  const dataType =
    paramTypeCell.typedValue?.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE
      ? convertTypedValueToDataParameterType(paramTypeCell.typedValue)
      : DEFAULT_DATA_PARAMETER_TYPE;

  const unitReferenceCell = row.cells[getColumnIndex(DataParameterConfigColumn.UNITS, columns)];
  if (
    dataType.type === FieldType.FLOAT &&
    unitReferenceCell.typedValue?.type === BaseTypedValueType.UNIT_REFERENCE
  ) {
    dataType.unit = unitReferenceCell.typedValue.unitSummary;
  }

  const minimum = cellMinMaxTypedValue(
    row.cells[getColumnIndex(DataParameterConfigColumn.MINIMUM, columns)],
    dataType.type
  );
  const maximum = cellMinMaxTypedValue(
    row.cells[getColumnIndex(DataParameterConfigColumn.MAXIMUM, columns)],
    dataType.type
  );

  const confirmationMethod = BenchlingConfig.PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES
    ? getConfirmationMethodValueFromCell(
        row.cells[getColumnIndex(DataParameterConfigColumn.CONFIRMATION_METHOD, columns)]
      )
    : ProcedureParameterConfirmationMethod.TABLE;

  return {
    dataParameterVersion: {
      cid: row.rowId,
      id: row.metadata.apiId,
      procedureDataParameterId: row.metadata.procedureDataParameterId,
      name: cellText(row.cells[getColumnIndex(DataParameterConfigColumn.NAME, columns)]).trim(),
      description: cellText(row.cells[getColumnIndex(DataParameterConfigColumn.DESCRIPTION, columns)]).trim(),
      dataType,
      minimum,
      maximum,
      confirmationMethod,
      libraryParameterDefinition: null,
    },
    values: [
      {
        id: row.metadata.plannedValueId,
        value: getDataParameterTypedValue(dataType),
        cid: `${row.rowId}-planned-value`,
      },
    ],
  };
}

// Check against all user-entered fields. Will never report "empty" if there is a backing model;
// this deliberately prevents users from blanking out fields on an existing object.
export function isDataParameterSettingEmpty(p: ProcedureDataParameterSetting): boolean {
  const setpoint = p.values[0];
  return (
    p.dataParameterVersion.id == null &&
    p.dataParameterVersion.name.length === 0 &&
    isMinMaxTypedValueEmpty(p.dataParameterVersion.minimum) &&
    isMinMaxTypedValueEmpty(p.dataParameterVersion.maximum) &&
    p.dataParameterVersion.description.length === 0 &&
    formatTypedValue(setpoint.value).length === 0 &&
    p.dataParameterVersion.confirmationMethod === ProcedureParameterConfirmationMethod.TABLE
  );
}

/**
 * Abstract base class for data parameters configuration tables in procedures.
 *
 * Class hierarchy:
 *   GenericStructuredTableAdapter
 *     └── BaseProcedureDataParametersConfigTableAdapter (this class)
 *           ├── ProcedureDataParametersConfigTableAdapter
 *           │     Standard implementation for data parameters without library support.
 *           └── ProcedureDataParametersConfigWithParamLibraryTableAdapter
 *                 Extended implementation that supports linking parameters to library definitions.
 *
 * This base class provides shared column definitions, row-to-setting conversion logic
 * (via buildCoreDataParameterSetting), and common table behaviors (CSV export, column
 * editability, row reordering). Subclasses can extend parameter settings with additional
 * fields like libraryParameterDefinition. We don't allow mixing library and non-library
 * parameters in the same table.
 */
export default abstract class BaseProcedureDataParametersConfigTableAdapter extends GenericStructuredTableAdapter<
  ProcedureDataParametersConfigTableMetadata,
  ProcedureDataParametersConfigTableRowMetadata
> {
  constructor(kind: StructuredTableKind, options?: StructuredTableAdapterOptions) {
    super(kind, {allowNoRowsInEntries: true, ...options});
  }

  getBlankRowMetadata(
    _tableMetadata: ProcedureDataParametersConfigTableMetadata
  ): ProcedureDataParametersConfigTableRowMetadata {
    return {apiId: null, procedureDataParameterId: null, plannedValueId: null};
  }

  getDisplayName(
    _table: StructuredTable<
      ProcedureDataParametersConfigTableMetadata,
      ProcedureDataParametersConfigTableRowMetadata
    >
  ): string {
    return '';
  }

  getRowDisplayName(
    _table: StructuredTable<
      ProcedureDataParametersConfigTableMetadata,
      ProcedureDataParametersConfigTableRowMetadata
    >
  ): string {
    return 'parameter';
  }

  override allowDeleteTable(
    _table: StructuredTable<
      ProcedureDataParametersConfigTableMetadata,
      ProcedureDataParametersConfigTableRowMetadata
    >
  ): {
    canDelete: boolean;
    reason?: string;
  } {
    return {canDelete: false};
  }

  override getActionFunctions(
    tableMetadata: ProcedureDataParametersConfigTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<
    ProcedureDataParametersConfigTableMetadata,
    ProcedureDataParametersConfigTableRowMetadata
  > {
    return {secondary: [downloadCSVActionFunction]};
  }

  override allowPasteTable(): boolean {
    return false;
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: ProcedureDataParametersConfigTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const enabledColumns = ALL_POSSIBLE_COLUMNS_ORDERED.filter(
      (c) => GET_IS_ENABLED_BY_COLUMN[c]?.() ?? true
    );
    return enabledColumns.map((c) => GET_STC_BY_COLUMN[c](metadata));
  }

  // We want to keep our columns editable even after the parameter is submitted and persisted
  override getNonFinalizingColumnTypes(): Array<TableColumnType> {
    return [
      ...super.getNonFinalizingColumnTypes(),
      TableColumnType.STRING,
      TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      TableColumnType.CUSTOM,
      TableColumnType.UNIT_REFERENCE,
      TableColumnType.OPTION_SELECTOR,
      TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN,
    ];
  }

  static getDelegateColumnByColumnAndDataType(
    this: void,
    dependentColumn: StructuredTableColumn,
    dataType: DataParameterDelegateType
  ): StructuredTableColumn {
    switch (dataType.type) {
      case FieldType.DATETIME:
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.CUSTOM,
            name: 'Delegate: datetime',
            isRequired: false,
          }),
          columnType: TableColumnType.CUSTOM,
          provenanceUuid: `${dependentColumn.provenanceUuid}_${dataType.type}`,
          customColumnConfig: {dataType: CustomColumnDataType.DATETIME},
        };
      case FieldType.TEXT:
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.CUSTOM,
            name: 'Delegate: text',
            isRequired: false,
          }),
          columnType: TableColumnType.CUSTOM,
          // The provenance ID must be stable. It's used to track the LSUV for any cell which uses this column
          provenanceUuid: `${dependentColumn.provenanceUuid}_${dataType.type}`,
          customColumnConfig: {dataType: CustomColumnDataType.TEXT},
        };
      case FieldType.FLOAT:
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.CUSTOM,
            name: 'Delegate: float',
            isRequired: false,
          }),
          columnType: TableColumnType.CUSTOM,
          provenanceUuid: `${dependentColumn.provenanceUuid}_${dataType.type}`,
          customColumnConfig: {dataType: CustomColumnDataType.FLOAT},
        };
      default:
        assert(false, 'Unknown data type', dataType);
    }
  }

  static makeCellFromDataParameter(
    dataParameterSetting: ProcedureDataParameterSetting,
    column: StructuredTableColumn,
    validationErrors: Array<ProcedureValidationError>,
    parameterUsages: Array<ParameterUsage>
  ): Cell {
    const minMaxCellError = (): Optional<EvaluatedCellError> =>
      validationErrorsToEvaluatedCellError(
        dataParameterSetting.dataParameterVersion.cid,
        ProcedureValidationObjectType.DATA_PARAMETER,
        filterProcedureValidationErrors(validationErrors, {
          errorTypes: [ProcedureValidationErrorType.INVALID_PARAMETER_RANGE],
        })
      );

    switch (column.provenanceUuid) {
      case columnProvenanceId(DataParameterConfigColumn.NAME): {
        const nameCellError = validationErrorsToEvaluatedCellError(
          dataParameterSetting.dataParameterVersion.cid,
          ProcedureValidationObjectType.DATA_PARAMETER,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [
              ProcedureValidationErrorType.NON_UNIQUE_NAME,
              ProcedureValidationErrorType.INVALID_NAME,
              // Exclude PARAMETER_LIBRARY_LINK_REQUIRED because it's handled by typed value resolution
              ProcedureValidationErrorType.PARAMETER_LIBRARY_LINK_DISALLOWED,
              ProcedureValidationErrorType.ARCHIVED_LIBRARY_PARAMETER_DEFINITION,
            ],
          })
        );
        return makeStringCell(dataParameterSetting.dataParameterVersion.name, {error: nameCellError});
      }

      case columnProvenanceId(DataParameterConfigColumn.TYPE):
        return {
          text: '',
          typedValue: convertDataParameterTypeToTypedValue(
            dataParameterSetting.dataParameterVersion.dataType
          ),
        };

      case columnProvenanceId(DataParameterConfigColumn.SETPOINT): {
        const setpointNotInRangeError = (): Optional<EvaluatedCellError> =>
          validationErrorsToEvaluatedCellError(
            dataParameterSetting.dataParameterVersion.cid,
            ProcedureValidationObjectType.DATA_PARAMETER,
            filterProcedureValidationErrors(validationErrors, {
              errorTypes: [ProcedureValidationErrorType.SETPOINT_NOT_IN_RANGE],
            })
          );
        return makeSetpointCell(dataParameterSetting, {
          error: setpointNotInRangeError(),
        });
      }

      case columnProvenanceId(DataParameterConfigColumn.UNITS):
        return makeUnitReferenceCell(dataParameterSetting);

      case columnProvenanceId(DataParameterConfigColumn.MINIMUM):
        return makeMinMaxCell(dataParameterSetting, dataParameterSetting.dataParameterVersion.minimum, {
          error: minMaxCellError(),
        });

      case columnProvenanceId(DataParameterConfigColumn.MAXIMUM):
        return makeMinMaxCell(dataParameterSetting, dataParameterSetting.dataParameterVersion.maximum, {
          error: minMaxCellError(),
        });

      case columnProvenanceId(DataParameterConfigColumn.DESCRIPTION):
        return makeStringCell(dataParameterSetting.dataParameterVersion.description);

      case columnProvenanceId(DataParameterConfigColumn.CONFIRMED_IN):
        return makeParameterUsageCell(parameterUsages, {
          confirmationMethod: dataParameterSetting.dataParameterVersion.confirmationMethod,
        });

      case columnProvenanceId(DataParameterConfigColumn.MEASURED_IN):
        return makeParameterUsageCell(parameterUsages, {
          usageType: ProcedureParameterUsageValueType.MEASUREMENT,
          confirmationMethod: dataParameterSetting.dataParameterVersion.confirmationMethod,
        });

      case columnProvenanceId(DataParameterConfigColumn.CONFIRMATION_METHOD):
        return makeConfirmationMethodCell(
          dataParameterSetting.dataParameterVersion.confirmationMethod ??
            // Adding a new row sets every cell to an empty typed value, we want it to default to table
            ProcedureParameterConfirmationMethod.TABLE
        );

      default:
        assert(false, 'Unknown table column', {column});
    }
  }

  override toTable(
    parametersConfigTable: StructuredTable<
      ProcedureDataParametersConfigTableMetadata,
      ProcedureDataParametersConfigTableRowMetadata
    >
  ): Table {
    const table = super.toTable(parametersConfigTable);
    return {
      ...table,
      lockedDims: {allowAddRows: true, allowRemoveRows: true, allowEditCols: false},
      allowColumnEdits: false,
    };
  }

  override allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: false,
    };
  }
}
